import React, { useContext, useRef } from 'react';

import { useDnd } from '@shared/hooks';
import { useMoveObject } from '@widgets/Files/hooks/useMoveObject';
import { ContextFiles } from '@widgets/Files/model/context';
import { IFIleEntity } from '@widgets/Files/model/types/fileEntity.interface';

export const useFileFolder = (folderInfo: IFIleEntity) => {
    const {
        onChangeModalMode,
        onChangeCurrentFolder,
        onChangeCurrentFolderInDropdown,
        onChangeCurrentDraggedFolder,
        currentDraggedFolder,
        onBreadCrumbClick: onFolderClick,
    } = useContext(ContextFiles);

    const dragPreviewRef = useRef<HTMLElement | null>(null);
    const moveObjectMutation = useMoveObject();
    const { isFileOver, handleDragLeave, handleDragEnter, handleDrop, handleDragOver } = useDnd({
        // mode: 'innerFiles',
        mode: 'folder',
        acceptDrop: (e) => {
            const types = Array.from(e.dataTransfer.types);
            if (
                currentDraggedFolder === folderInfo.id &&
                types.includes('text/internal-file') &&
                types.includes('data-folder-id')
            )
                return false;
            return types.includes('text/internal-file');
        },
    });

    const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
        const objectId =
            e.dataTransfer.getData('data-file-id') || e.dataTransfer.getData('data-folder-id');
        const type = e.dataTransfer.getData('file-type') as 'folder' | 'file';
        if (currentDraggedFolder === folderInfo.id) {
            onChangeCurrentDraggedFolder(null);
            return;
        }
        const moveToId = folderInfo.id;
        moveObjectMutation.mutate({ objectId, type, moveToId });
        handleDrop(e);
        onChangeCurrentDraggedFolder(null);
    };

    const handleDragStart = (e: React.DragEvent<HTMLDivElement>) => {
        const previewWrapper = document.createElement('div');
        previewWrapper.style.width = '72px';
        previewWrapper.style.height = '72px';
        previewWrapper.style.background = 'white';
        previewWrapper.style.display = 'flex';
        previewWrapper.style.alignItems = 'center';
        previewWrapper.style.justifyContent = 'center';
        previewWrapper.style.borderRadius = '8px';
        previewWrapper.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
        previewWrapper.style.position = 'absolute';
        previewWrapper.style.top = '-9999px';
        previewWrapper.style.left = '-9999px';

        let previewContent: HTMLElement;

        const img = document.createElement('img');
        img.src = folderInfo.previewSvgUrl;
        img.style.width = '100%';
        img.style.height = '100%';
        img.style.objectFit = 'contain';
        previewContent = img;

        previewWrapper.appendChild(previewContent);
        document.body.appendChild(previewWrapper);

        dragPreviewRef.current = previewWrapper;

        e.dataTransfer.setData('text/internal-file', String(folderInfo.id));
        e.dataTransfer.setDragImage(previewWrapper, 36, 36);
        e.dataTransfer.setData('data-folder-id', String(folderInfo.id));
        e.dataTransfer.setData('file-type', String('folder'));

        onChangeCurrentDraggedFolder(folderInfo.id);
    };

    return {
        onChangeModalMode,
        onChangeCurrentFolder,
        onChangeCurrentFolderInDropdown,
        onFolderClick,
        onDrop,
        isFileOver,
        handleDragLeave,
        handleDragEnter,
        handleDragStart,
        handleDragOver,
    };
};
